import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { DSData, DSSetup, Service } from 'webapp-types';
import {
	FilterItemType,
	FilterLogicalOperator,
	MetricFilterOperator,
	Unit,
} from 'webapp/enums/enums';
import { copyObject, equal, serializeFilters, uniq } from 'webapp/utils/common';

import type { Metric, MetricFromClient } from 'webapp/models-custom';
import FilterGroup from 'webapp/models-custom/filter/filter-group';
import type FilterItem from 'webapp/models-custom/filter/filter-item';
import type { MetricForMasterFilter } from 'webapp/models-custom/metric-setup-for-master-filter';
import Serviced from 'webapp/models-custom/serviced';

export interface ChangeableProps {
	key: string | null;
	spaceAccessId: number | null;
	dimensions?: string[];
	filters?: DSSetup.MetricSetupFiltersData;
}

export type OnChangeProp = keyof ChangeableProps;
export type OnChangePropValue = ChangeableProps[keyof ChangeableProps];

// used to inform "outside" about changes caused by inner logic,
// e.g. resetting attributes on mk change
// by default, do nothing (some components / models are still using observers)
export type OnChangeFn = (_prop: OnChangeProp, _value: OnChangePropValue) => void;

export interface MetricSetupBaseProps {
	key?: string | null;
	spaceAccessId?: number | null;
	dimensions?: string[];
	filters?: DSSetup.MetricSetupFiltersData | null;
}

export default abstract class MetricSetupBase extends Serviced {
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	onChange: OnChangeFn | null = null;

	constructor(props: MetricSetupBaseProps = {}) {
		super();

		if (props.key) {
			this._key = props.key;
		}

		if (props.spaceAccessId !== undefined) {
			this._spaceAccessId = props.spaceAccessId;
		}
	}

	// --------- KEY + SAID + METRIC ----------

	@tracked protected _key = '';
	@tracked protected _spaceAccessId: number | null = null;

	get key(): string | null {
		return this._key;
	}

	set key(value: string) {
		if (value === this._key) {
			return;
		}

		this._key = value;
		this.notifyChange('key', value);

		this.resetDimensionsAndFilters();
	}

	get spaceAccessId(): number | null {
		return this._spaceAccessId;
	}

	set spaceAccessId(value: number | null) {
		if (value === this._spaceAccessId) {
			return;
		}

		this._spaceAccessId = value;
		this.notifyChange('spaceAccessId', value);
	}

	abstract get metric(): Metric | MetricFromClient | MetricForMasterFilter | null;

	protected afterMetricSet(props: MetricSetupBaseProps, onChange?: OnChangeFn) {
		// called only in on constructor
		// seperate method bcos we set metric differently for normal and -for-client
		if (props.dimensions?.length) {
			this._dimensions = props.dimensions;
		} else if (this.metric?.autoselectedDimension) {
			this._dimensions = [this.metric.autoselectedDimension];
			if (onChange) {
				onChange('dimensions', this._dimensions);
			}
		}

		if (props.filters && Object.keys(props.filters || {}).length) {
			this.initializeFilters(props.filters);
		}

		if (onChange) {
			// setting this at the end, so all previous changes do not trigger notifyChange
			this.onChange = onChange;
		}
	}

	// -------- DIMENSIONS --------

	@tracked protected _dimensions: string[] = [];

	get dimensions(): string[] {
		return this._dimensions;
	}

	set dimensions(value: string[]) {
		let validDimensions = value;

		if (!value.length) {
			const autoselectedDimension = this.metric?.autoselectedDimension;
			if (autoselectedDimension) {
				validDimensions = [autoselectedDimension];
			}
		}

		if (equal(validDimensions, this._dimensions)) {
			return;
		}

		this._dimensions = validDimensions;
		this.notifyChange('dimensions', validDimensions);
	}

	get allowDimensions(): boolean {
		return this.metricHasDimensions;
	}

	get metricHasDimensions(): boolean {
		// just means that we can select a dimension
		// (e.g. Sessions is standalone, but has dimensions)
		return !!this.metric?.allowDimensions;
	}

	get mustSelectDimension(): boolean {
		return !!this.metric?.mustSelectDimension;
	}

	get isDimensionSelected(): boolean {
		return this.dimensions.length > 0;
	}

	get isMultipleDimensionsSelected(): boolean {
		return this.dimensions.length > 1;
	}

	get allowBasicDimensionValueFilters(): boolean {
		// for basic filtering only - meaning filter "currently selected dimension IS": ...
		// for advanced filtering we need block (feature) & data-select (isDataset) context
		// see data-select.allowAdvancedSetup
		// this can still be true if feature allows advanced filtering!
		return this.isValidDimensionSelected;
	}

	private get isValidDimensionSelected(): boolean {
		return this.allowDimensions && this.isDimensionSelected;
	}

	get metricDimensions() {
		return this.metric?.dimensions || [];
	}

	getDimensionNameForDimension(dimension: string) {
		return this.metricDimensions?.find((d) => d.key === dimension)?.value || dimension;
	}

	// -------- FILTERS --------
	// filterGroupsLogicalOperatornot changeable for now
	// filterGroups should not be changed manually, all goes through unitName setter
	@tracked readonly filterGroupsLogicalOperator = FilterLogicalOperator.AND;
	@tracked filterGroups: FilterGroup[] = [];

	@cached
	get dimensionFilterGroup(): FilterGroup | undefined {
		// for now, only one group (possible second is units which is controlled differently)
		return this.filterGroups.find((g) => {
			return g.filterItems.find((f) => f.isTypeDimension);
		});
	}

	@cached
	get filtersData(): DSSetup.MetricSetupFiltersData | null {
		const data = {
			logicalOperator: this.filterGroupsLogicalOperator,
			groupItems: copyObject(this.filterGroups.map((d) => d.filterGroupData)),
		};
		return data.groupItems.length ? data : null;
	}

	@cached
	get isAdvancedFilterSelected() {
		if (this.isMultipleDimensionsSelected) {
			return true; // just in case. the condition below checks only first dimension
		}
		const selectedDimension = this.dimensions[0];
		const dimensionItems = this.dimensionFilterGroup?.filterItems || [];
		return (
			dimensionItems.length > 1 ||
			dimensionItems.some((i) => {
				return i.isAdvancedFilter || ![selectedDimension].includes(i.field);
			})
		);
	}

	@cached
	get isAdvancedSetupSelected() {
		return this.isMultipleDimensionsSelected || this.isAdvancedFilterSelected;
	}

	@cached
	get attributeNamesFromBasicFilters(): string[] {
		const filterGroup = this.dimensionFilterGroup,
			dimensions = this.dimensions;
		if (filterGroup) {
			const names: string[] = [];
			filterGroup.filterItems.forEach((item) => {
				dimensions.forEach((dimension) => {
					if (item.checkIfIsBasicFilterForDimension(dimension)) {
						names.push(...item.values);
					}
				});
			});
			return uniq(names);
		}
		return [];
	}

	get firstAttributeNameFromBasicFilters(): string {
		// FOR LEGACY (NON-ADVANCED) ONLY
		return this.attributeNamesFromBasicFilters[0] || '';
	}

	@cached
	get unapplicableFilterItems(): FilterItem[] {
		// FOR LEGACY (NON-ADVANCED) ONLY
		// only "selected breakdown IS ..." can be applied; with no valid breakdown, none can
		const selectedDimension = this.isValidDimensionSelected ? this.dimensions[0] : undefined;
		const dimensionItems = this.dimensionFilterGroup?.filterItems || [];
		return dimensionItems.filter((i) => {
			return !selectedDimension || !i.checkIfIsBasicFilterForDimension(selectedDimension);
		});
	}

	@cached
	get filtersDataWithoutUnapplicableItems(): DSSetup.MetricSetupFiltersData | null {
		// FOR LEGACY (NON-ADVANCED) ONLY
		const unapplicableFilterItems = this.unapplicableFilterItems;
		const groupItems = this.filterGroups
			.map((g) => ({
				logicalOperator: g.logicalOperator,
				filterItems: g.filterItems
					.filter((i) => !unapplicableFilterItems.includes(i))
					.map((i) => i.filterItemData),
			}))
			.filter((g) => g.filterItems.length);
		return groupItems.length
			? {
					logicalOperator: this.filterGroupsLogicalOperator,
					groupItems: copyObject(groupItems),
				}
			: null;
	}

	get dimensionFilterItemsCount() {
		return this.dimensionFilterGroup?.filterItems.length || 0;
	}

	get unitFilterItemsCount() {
		return this.unitFilterGroup?.filterItems.length || 0;
	}

	get dimensionFilterItemsCountText() {
		const count = this.dimensionFilterItemsCount;

		let text = 'Filter';
		if (count !== 1) {
			text += 's';
		}
		if (count > 0) {
			text += ` (${count})`;
		}
		return text;
	}

	setFiltersFromData(filters?: DSSetup.MetricSetupFiltersData | null) {
		this.initializeFilters(filters);
	}

	private initializeFilters(data?: DSSetup.MetricSetupFiltersData | null) {
		const filterGroups: FilterGroup[] = [];
		data?.groupItems.forEach((g) => {
			// dont recreate when empty -- happens when we remove the last filter item in group
			if (g.filterItems.length) {
				filterGroups.push(this.createFilterGroupInstance(g));
			}
		});
		this.filterGroups = filterGroups;
		this.notifyFilterChange();
	}

	protected createFilterGroupInstance(data: DSSetup.MetricSetupFilterGroupData): FilterGroup {
		return new FilterGroup(data, () => {
			this.initializeFilters(this.filtersData);
			this.notifyFilterChange();
		});
	}

	private mapAttributeAndUnitNamesToFiltersData(attributes: string[], unit = Unit.ALL) {
		// FOR LEGACY (NON-ADVANCED) ONLY

		const filtersData: DSSetup.MetricSetupFiltersData = {
			logicalOperator: FilterLogicalOperator.AND,
			groupItems: [],
		};

		if (attributes.length) {
			filtersData.groupItems.push({
				logicalOperator: FilterLogicalOperator.AND,
				filterItems: [
					{
						type: FilterItemType.DIMENSION,
						field: this.dimensions[0]!,
						operator: MetricFilterOperator.ANY_OF,
						values: attributes,
					},
				],
			});
		}

		if (this.hasUnits && typeof unit === 'string' && unit !== Unit.ALL) {
			filtersData.groupItems.push({
				logicalOperator: FilterLogicalOperator.AND,
				filterItems: [
					{
						type: FilterItemType.UNIT,
						field: 'unit',
						operator: MetricFilterOperator.ANY_OF,
						values: [unit],
					},
				],
			});
		}

		return filtersData;
	}

	addFilterGroup(filterGroupData: DSSetup.MetricSetupFilterGroupData) {
		const filterGroup = this.createFilterGroupInstance(filterGroupData);
		this.filterGroups = [...this.filterGroups, filterGroup];
		this.notifyFilterChange();
	}

	protected removeFilterGroup(filterGroup: FilterGroup) {
		this.filterGroups = this.filterGroups.filter((g) => g !== filterGroup);
		this.notifyFilterChange();
	}

	@cached
	get metricOperatorLimitations(): MetricFilterOperator[] {
		const metricData = this.metricData;
		// if not requested yet, all are valid
		const limitations = this.mmMetricsService.getCachedOperatorLimitations(
			metricData ? [metricData] : [],
		);
		return (
			limitations ||
			this.mmMetricsService.constantsService.metricFilterOperators.map(
				(i) => i.value as MetricFilterOperator,
			)
		);
	}

	getFirstAvailableMetricOperator() {
		const operators = this.metricOperatorLimitations;
		if (operators.includes(MetricFilterOperator.ANY_OF)) {
			return MetricFilterOperator.ANY_OF;
		} else {
			return operators[0]!;
		}
	}

	// -------- UNITS (through filters) --------

	abstract get defaultUnit(): string;

	get unitName(): string {
		return this.unitFilterGroup?.filterItems[0]?.values[0] ?? this.defaultUnit;
	}

	set unitName(value: string | null) {
		if (value && value !== Unit.ALL && this.hasUnits) {
			const unitFilterItem = this.unitFilterGroup?.filterItems[0];
			if (unitFilterItem) {
				// change
				unitFilterItem.values = [value];
			} else {
				// add
				this.addFilterGroup({
					logicalOperator: FilterLogicalOperator.AND,
					filterItems: [
						{
							type: FilterItemType.UNIT,
							field: 'unit',
							operator: MetricFilterOperator.ANY_OF,
							values: [value],
						},
					],
				});
			}
		} else {
			// remove
			const unitFilterGroup = this.unitFilterGroup;
			if (unitFilterGroup) {
				this.removeFilterGroup(unitFilterGroup);
			}
		}
	}

	@cached
	get unitFilterGroup(): FilterGroup | undefined {
		return this.filterGroups.find((g) => {
			return g.filterItems.find((f) => f.isTypeUnit);
		});
	}

	get specificSelectedUnit() {
		const selectedUnit = this.selectedUnit;
		if ([Unit.ALL, Unit.EMPTY].includes(selectedUnit)) {
			return '';
		} else {
			return selectedUnit;
		}
	}

	get selectedUnit(): string {
		return this.unitName;
	}

	get allowUnitNameFilter(): boolean {
		return this.hasUnits;
	}

	get hasUnits(): boolean {
		return !!this.metric?.hasUnits;
	}

	setFiltersFromAttributeNames(values: string[]) {
		// FOR LEGACY (NON-ADVANCED) ONLY

		// keeps currently selected unit
		const filtersData = this.mapAttributeAndUnitNamesToFiltersData(values, this.unitName);
		this.initializeFilters(filtersData);
	}

	setFiltersFromAttributeName(value?: string | null) {
		// FOR LEGACY (NON-ADVANCED) ONLY

		const values = value ? [value] : [];
		this.setFiltersFromAttributeNames(values);
	}

	setFiltersFromDimensionsAndValuesMapAndUnit(
		dimensionValues: DSData.DimensionsAndValuesMap,
		unit?: string,
	) {
		const filtersData: DSSetup.MetricSetupFiltersData = {
			logicalOperator: FilterLogicalOperator.AND,
			groupItems: [],
		};

		const dimensionsAndValuesEntries = Object.entries(dimensionValues);
		if (dimensionsAndValuesEntries.length) {
			filtersData.groupItems.push({
				logicalOperator: FilterLogicalOperator.AND,
				filterItems: dimensionsAndValuesEntries.map(([field, value]) => ({
					type: FilterItemType.DIMENSION,
					field,
					operator: MetricFilterOperator.ANY_OF,
					values: [value],
				})),
			});
		}

		if (this.hasUnits && typeof unit === 'string' && unit !== Unit.ALL) {
			filtersData.groupItems.push({
				logicalOperator: FilterLogicalOperator.AND,
				filterItems: [
					{
						type: FilterItemType.UNIT,
						field: 'unit',
						operator: MetricFilterOperator.ANY_OF,
						values: [unit],
					},
				],
			});
		}

		this.initializeFilters(filtersData);
	}

	// ------ SETUP get & set -------

	get metricData(): DSSetup.MetricData | null {
		// reading from props, not this.metric
		// bcos we don't always have metric available (e.g. public url)
		const key = this.key;

		if (!key) {
			return null;
		}

		return { key, spaceAccessId: this.spaceAccessId };
	}

	get lineageMetricKey(): string | null {
		const key = this.key;

		if (!key) {
			return null;
		}

		return this.mmMetricsService.getLineageKey(key, this.spaceAccessId);
	}

	get metricSetupWithoutFiltersData(): DSSetup.MetricSetupWithoutFiltersData | null {
		const metricData = this.metricData;
		if (metricData) {
			const metricSetupData: DSSetup.MetricSetupData = copyObject(metricData);
			metricSetupData.dimensions = this.dimensions;

			return metricSetupData;
		}
		return null;
	}

	get metricSetupDataForFetchDimensionValues(): DSSetup.MetricSetupWithoutFiltersData[] {
		const metricSetupData = this.metricSetupWithoutFiltersData;
		return metricSetupData ? [metricSetupData] : [];
	}

	@cached
	get metricSetupData(): DSSetup.MetricSetupData | null {
		const metricSetupWithoutFiltersData = this.metricSetupWithoutFiltersData;
		if (metricSetupWithoutFiltersData) {
			const metricSetupData: DSSetup.MetricSetupData = copyObject(
				metricSetupWithoutFiltersData,
			);
			if (this.filterGroups.length) {
				metricSetupData.filters = this.filtersData || undefined;
			}
			return metricSetupData;
		}
		return null;
	}

	@cached
	get serializedMetricSetupData(): DSSetup.MetricSetupData | null {
		const metricSetupData = this.metricSetupData;
		if (metricSetupData?.filters) {
			metricSetupData.filters = serializeFilters(metricSetupData.filters) || undefined;
		}
		return metricSetupData;
	}

	setFromMetricData(metricData: DSSetup.MetricData | null) {
		const key = metricData?.key || '';
		const spaceAccessId = metricData?.spaceAccessId || null;

		this.spaceAccessId = spaceAccessId;
		this.key = key;
	}

	setFromMetricSetupData(metricSetupData: DSSetup.MetricSetupData | null) {
		this.setFromMetricData(metricSetupData);
		this.dimensions = metricSetupData?.dimensions || [];
		this.setFiltersFromData(metricSetupData?.filters || null);
	}

	// --------- onChange notifier ----------

	protected notifyChange(prop: OnChangeProp, value: OnChangePropValue): void {
		if (this.onChange) {
			this.onChange(prop, value);
		}
	}

	protected notifyFilterChange() {
		this.notifyChange('filters', this.filtersData);
	}

	protected resetDimensionsAndFilters(): void {
		this.dimensions = [];
		this.resetFilters();
	}

	resetFilters() {
		this.filterGroups = [];
		this.notifyFilterChange();
	}
}
